import { ChangeDetectorRef, Component, forwardRef, OnDestroy, OnInit } from "@angular/core";
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  BehaviorSubject,
  combineLatestWith,
  filter,
  map,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { ArchiveOrganizationDialog } from "src/app/shared/components/archive-organization-dialog/archive-organization-dialog";

import { Checkbox } from "../../shared/components/checkmarks/checkmarks.component";
import { DialogService } from "../../shared/components/dialog/dialog.service";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { getLangLabel, isUiLangEnabled } from "../../shared/constants/lang-labels";
import { LangsService } from "../../shared/services/langs.service";
import { capitalize } from "../../shared/tool-functions/capitalize";
import { unlimitedCreditsSubscriptionPlans } from "../../shared/tool-functions/constants";
import { buildCreditInfo } from "../../shared/tool-functions/credit.constants";
import { safeNumber } from "../../shared/tool-functions/safe-number";
import { CompanyOfferCard, OfferPlanCode } from "../../shared/types/offer-upsell";
import { EntityFormGroup } from "../../shared/types/entity-form-group";
import { Lang } from "../../shared/types/lang";
import { DynamicType } from "../../thematics/models/dynamic.model";
import { Thematic } from "../../thematics/models/thematic.model";
import { ThematicRepository } from "../../thematics/repositories/thematic.repository";
import { Role } from "../../users/models/users.entity";
import { ProfileService } from "../../users/services/profile.service";
import { AddUnitsComponent } from "../add-units/add-units.component";
import { EditOrganizationNameComponent } from "../edit-organization-name/edit-organization-name.component";
import {
  CreateOrganizationSubscription,
  Organization,
  SubscriptionPlanCatalogItem,
} from "../models/organizations.entity";
import { NewOrganizationComponent } from "../new-organization/new-organization.component";
import { OrganizationMembersRepository } from "../repositories/organization.members.repository";
import { OrganizationRepository } from "../repositories/organization.repository";
import {
  ADMIN_LEGACY_FORMULA_OPTIONS,
  getPlanSlugByDisplayName,
} from "../constants/subscription-plan.constants";
import {
  getCompanyOfferCard,
  getOfferContextSnapshot,
  getRecommendedOfferPlan,
} from "../utils/plan-offer-marketing";
import { flattenOrganizationTrees, getOrganizationPlanMetadataByPlanTagname } from "../utils/utils";

@Component({
  selector: "app-organization-edition-form",
  templateUrl: "./organization-edition-form.component.html",
  styleUrl: "./organization-edition-form.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => OrganizationEditionFormComponent),
    },
  ],
})
export class OrganizationEditionFormComponent
  implements ControlValueAccessor, OnDestroy, OnInit {
  organization!: Organization;

  organizationFormGroup!: EntityFormGroup<{
    name: string;
    allowedOrganizationIds: string[];
    forbiddenOrganizationIds: string[];
    extraOrganizationMatching: boolean;
    intraOrganizationMatching: boolean;
    forbiddenThematicIds: string[];
    speechEnabled: boolean;
    coachingEnabled: boolean;
    talkerQuota: string;
    unlimitedQuota: boolean;
    yearlyTalkerQuota: string;
    testEndDate: string;
    managedSubscriptionPlan: string;
    managedSubscriptionStartsAt: string;
    managedSubscriptionSeatCount: number;
    defaultLangId: string;
  }>;

  availableLangs: Lang[] = [];

  readonly getLangLabel = getLangLabel;

  readonly isUiLangEnabled = isUiLangEnabled;

  allThematics: Thematic[] = [];

  forbiddenOrganizationsCheckboxes: Checkbox[] = [];

  forbiddenOrganizationIdsSelection: string[] = [];

  allowedOrganizationIdsSelection: string[] = [];

  quotaPossibilities = ["1 parcours", "2 parcours", "3 parcours", "4 parcours", "5 parcours", "6 parcours", "7 parcours", "8 parcours", "9 parcours", "10 parcours"];

  quotaYearPossibilities = ["ans"];

  loading = true;

  settingValues = false;

  private onChange: (value: any) => void = (_: any) => {};

  private onTouched: () => void = () => {};

  destroy$ = new Subject<void>();

  isDisabled = false;

  profile!: any;

  canAddUnits = false;

  units!: number;

  plans: string[] = [ ...ADMIN_LEGACY_FORMULA_OPTIONS ];

  managedFormulaNames: string[] = [];

  private readonly managedFormulaCodes = [ "wetalk", "wetalk-plus", "wetalk-coach" ];

  private managedPlanVersionIdsByName: Record<string, string> = {};

  canSeePage = true;

  private organizationSubject = new BehaviorSubject<any>(null);

  organization$ = this.organizationSubject.asObservable();


  private subscriptionPlanMetadataSubject = new BehaviorSubject<any>(undefined);

  subscriptionPlanMetadata$ = this.subscriptionPlanMetadataSubject.asObservable();

  // Reactive subscriptionButtonLabel
  subscriptionButtonLabel$ = this.organization$.pipe(
    map(organization => (organization?.subscriptionPlan ? organization.subscriptionPlan.name : 'Ajouter une formule'))
  );

  // Reactive subscriptionButtonIcon
  subscriptionButtonIcon$ = this.organization$.pipe(
    map(organization => (organization?.subscriptionPlan ? 'edit' : 'add'))
  );
  
  private isTesterPlanSubject = new BehaviorSubject<boolean>(false);

  isTesterPlan$ = this.isTesterPlanSubject.asObservable();

  // private readonly tracker = inject(MatomoTracker);

  ngOnDestroy() {
    this.destroy$.next();
  }

  registerOnChange(fn: (value: Organization) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: Organization): void {
    const forbiddenOrganizationIds =
      value.forbiddenOrganizations?.map((o) => o.id) ?? [];

    this.organizationSubject.next(value);
    this.organization = value;
    this.units = value.units;
    this.organizationMembersRepository.setOrganizationId(value.id);
    const capabilityOrganizationSettings = this.organization?.capabilities?.organization_settings;
    this.canSeePage = this.profile?.role === Role.Admin
      ? true
      : capabilityOrganizationSettings !== undefined
        ? capabilityOrganizationSettings
        : !(this.profile.role === Role.Talker &&
          (!this.profile.organization.subscriptionPlan ||
            ['personalized-subscription-individual', 'personalized-subscription-professional'].includes(this.profile.organization.subscriptionPlan.slug)) &&
          (!this.organization?.subscriptionPlan ||
            ['personalized-subscription-individual', 'personalized-subscription-professional'].includes(this.organization?.subscriptionPlan?.slug)));
    
    const subscriptionPlanMetadata = this.organization.subscriptionPlan?.slug ? getOrganizationPlanMetadataByPlanTagname(this.organization.subscriptionPlan?.slug) : undefined;
    this.subscriptionPlanMetadataSubject.next(subscriptionPlanMetadata);
    this.isTesterPlanSubject.next(this.organization.subscriptionPlan?.slug === 'tester-subscription');    

    this.organizationFormGroup.controls.name.setValue(value.name, {
      emitEvent: false,
      onlySelf: true,
    });
    this.organizationFormGroup.controls.allowedOrganizationIds.setValue(
      this.organizationFormGroup.controls.allowedOrganizationIds.value.filter(
        (id) => !forbiddenOrganizationIds.includes(id)
      ),
      {
        emitEvent: false,
        onlySelf: true,
      }
    );
    this.organizationFormGroup.controls.forbiddenOrganizationIds.setValue(
      forbiddenOrganizationIds,
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.extraOrganizationMatching.setValue(
      value.extraOrganizationMatching,
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.intraOrganizationMatching.setValue(
      value.intraOrganizationMatching,
      { emitEvent: false }
    );
    // Set dynamic type enabled checkboxes based on forbiddenDynamicTypes
    // If 'speech' is in forbiddenDynamicTypes, speechEnabled should be false
    const forbiddenDynamics = value.forbiddenDynamicTypes ?? [];
    this.organizationFormGroup.controls.speechEnabled.setValue(
      !forbiddenDynamics.includes(DynamicType.SPEECH),
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.coachingEnabled.setValue(
      !forbiddenDynamics.includes(DynamicType.COACHING),
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.talkerQuota.setValue(
      value.talkerQuota !== null ? `${String(value.talkerQuota)} parcours` : "",
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.unlimitedQuota.setValue(
      value.talkerQuota === null,
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.managedSubscriptionPlan.setValue(
      value.activeSubscription
        ? this.getManagedPlanDisplayName(value.activeSubscription.planCode)
        : "",
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.managedSubscriptionStartsAt.setValue(
      value.activeSubscription?.startsAt
        ? new Date(value.activeSubscription.startsAt).toISOString().split("T")[0]
        : this.getTodayDate(),
      { emitEvent: false }
    );
    this.organizationFormGroup.controls.managedSubscriptionSeatCount.setValue(
      value.activeSubscription?.seatCount ?? Math.max(value.members?.length ?? 0, 1),
      { emitEvent: false }
    );
  }

  setDisabledState(state: boolean) {
    this.isDisabled = state;
  }

  constructor(
    private readonly organizationRepository: OrganizationRepository,
    private readonly organizationMembersRepository: OrganizationMembersRepository,
    private readonly profileService: ProfileService,
    private readonly langsService: LangsService,
    private readonly snackBarService: SnackbarService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly formBuilder: FormBuilder,
    private readonly thematicRepository: ThematicRepository,
    private readonly dialog: DialogService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.organizationFormGroup = this.formBuilder.group({
      name: new FormControl("", {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      allowedOrganizationIds: new FormControl([] as string[], {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      forbiddenOrganizationIds: new FormControl([] as string[], {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      forbiddenThematicIds: new FormControl([] as string[], {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      speechEnabled: new FormControl(true, {
        nonNullable: true,
      }),
      coachingEnabled: new FormControl(true, {
        nonNullable: true,
      }),
      extraOrganizationMatching: new FormControl(true, {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      intraOrganizationMatching: new FormControl(false, {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      talkerQuota: new FormControl("1", {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      unlimitedQuota: new FormControl(false, {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      yearlyTalkerQuota: new FormControl("", {
        validators: Validators.min(1),
        nonNullable: true,
      }),
      testEndDate: new FormControl("", {
        nonNullable: true
      }),
      managedSubscriptionPlan: new FormControl("", {
        nonNullable: true
      }),
      managedSubscriptionStartsAt: new FormControl(this.getTodayDate(), {
        nonNullable: true
      }),
      managedSubscriptionSeatCount: new FormControl(1, {
        validators: Validators.required,
        nonNullable: true
      }),
      defaultLangId: new FormControl("", {
        nonNullable: true,
      }),
    });
  }

  ngOnInit() {
    this.langsService
      .getLangs()
      .pipe(takeUntil(this.destroy$))
      .subscribe((langs) => {
        this.availableLangs = [...langs].sort((a, b) =>
          getLangLabel(a.name).localeCompare(getLangLabel(b.name), 'fr')
        );
      });

    this.organizationRepository
      .getSubscriptionPlans()
      .pipe(takeUntil(this.destroy$))
      .subscribe((plans) => {
        const managedPlans = this.managedFormulaCodes
          .map((planCode) => plans.find((plan) => plan.slug === planCode))
          .filter((plan): plan is SubscriptionPlanCatalogItem => !!plan);
        this.managedFormulaNames = managedPlans.map((plan) =>
          this.getManagedPlanDisplayName(plan.slug)
        );
        this.managedPlanVersionIdsByName = managedPlans.reduce((acc, plan) => {
          const latestVersion = [ ...(plan.versions ?? []) ].sort((versionA, versionB) =>
            new Date(versionB.effectiveFrom).getTime() -
            new Date(versionA.effectiveFrom).getTime()
          )[0];
          if (latestVersion) {
            acc[this.getManagedPlanDisplayName(plan.slug)] = latestVersion.id;
          }
          return acc;
        }, {} as Record<string, string>);
      });
    
    this.organizationRepository
      .findAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe((organizations) => {
        if (organizations.length > 1) {
          this.canAddUnits = true;
        }
      });

    this.profileService
      .getMyProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.profile = profile;
      });

    this.thematicRepository
      .getAll()
      .pipe(
        combineLatestWith(this.organizationRepository.findAll()),
        takeUntil(this.destroy$)
      )
      .subscribe(([thematics, allOrganizations]) => {
        this.organizationFormGroup.controls.allowedOrganizationIds.setValue(
          allOrganizations.map((o) => o.id),
          {
            emitEvent: false,
            onlySelf: true,
          }
        );
        this.settingValues = true;
        this.organizationFormGroup.controls.intraOrganizationMatching.setValue(
          this.organization.intraOrganizationMatching,
          {
            emitEvent: false,
            onlySelf: true,
          }
        );
        this.organizationFormGroup.controls.extraOrganizationMatching.setValue(
          this.organization.extraOrganizationMatching,
          {
            emitEvent: false,
            onlySelf: true,
          }
        );
        this.organizationFormGroup.controls.talkerQuota.setValue(
          this.organization.talkerQuota
            ? `${String(this.organization.talkerQuota)  } parcours`
            : "",
          {
            emitEvent: false,
            onlySelf: true,
          }
        );

        this.organizationFormGroup.controls.yearlyTalkerQuota.setValue(
          this.organization.talkerQuota
            ? this.organization.yearlyTalkerQuota ? "ans" : ""
            : "",
          {
            emitEvent: false,
            onlySelf: true,
          }
        );

        this.organizationFormGroup.controls.unlimitedQuota.setValue(
          this.organization.talkerQuota === null,
          {
            emitEvent: false,
            onlySelf: true,
          }
        );

        this.organizationFormGroup.controls.testEndDate.setValue(
          this.organization.testEndDate ? new Date(this.organization.testEndDate).toISOString().split('T')[0] : this.getDefaultTestEndDate()
        );

        this.organizationFormGroup.controls.defaultLangId.setValue(
          this.organization.defaultLang?.id ?? "",
          {
            emitEvent: false,
            onlySelf: true,
          }
        );

        this.forbiddenOrganizationIdsSelection =
          this.organization.forbiddenOrganizations?.map((org) => org.id) ?? [];
        this.allThematics = thematics;
        this.loading = false;
        this.settingValues = false;

        this.forbiddenOrganizationIdsSelection =
          this.organization.forbiddenOrganizations?.map(({ id }) => id) ?? [];
        this.allowedOrganizationIdsSelection = allOrganizations
          .map(({ id }) => id)
          .filter(
            (id) =>
              id !== this.organization.id &&
              !this.forbiddenOrganizationIdsSelection.includes(id)
          );
        this.forbiddenOrganizationsCheckboxes = allOrganizations
          .sort((organizationA, organizationB) => {
            if (organizationA.name < organizationB.name) {
              return -1;
            }
            return 1;
          })
          .filter(({ id }) => id !== this.organization.id)
          .map(({ id, name }) => ({
            id,
            key: name.toUpperCase(),
            selected: !!this.forbiddenOrganizationIdsSelection?.includes(id),
          }));
      });

    this.organizationFormGroup.controls.talkerQuota.valueChanges
      .pipe(
        filter((quota) => !!quota),
        tap((value) => {
          (window._mtm || []).push({ 'event': 'nombre-quota', 'quota': `${value}` });
          this.organizationFormGroup.controls.unlimitedQuota.setValue(false, {
            emitEvent: false,
            onlySelf: true,
          });
        }),
        switchMap((quota) =>this.organizationRepository.patch(this.organization.id, {
            talkerQuota:
              quota !== undefined && !quota
                ? null
                : Number(quota.split(" ")[0])
                ? Number(quota.split(" ")[0])
                : undefined,
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.snackBarService.pushMessage("Modification enregistrée", "success");
      });

    this.organizationFormGroup.controls.defaultLangId.valueChanges
      .pipe(
        filter(() => !this.settingValues),
        switchMap((defaultLangId) =>
          this.organizationRepository.patch(this.organization.id, {
            defaultLangId: defaultLangId || null,
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((organization) => {
        this.organization.defaultLang = organization.defaultLang ?? null;
        this.snackBarService.pushMessage("Modification enregistrée", "success");
      });

      this.organizationFormGroup.controls.yearlyTalkerQuota.valueChanges
      .pipe(
        switchMap((yearlyQuota) => this.organizationRepository.patch(this.organization.id, {
            yearlyTalkerQuota: yearlyQuota === "ans",
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.snackBarService.pushMessage("Modification enregistrée", "success");
      });

    this.organizationFormGroup.controls.unlimitedQuota.valueChanges
      .pipe(
        tap((unlimitedQuota) => {
          (window._mtm || []).push({ 'event': 'clic-checkbox', 'checkbox-type': 'Illimité' });
          this.organizationFormGroup.controls.talkerQuota.setValue(
            unlimitedQuota ? "" : "1",
            { emitEvent: false, onlySelf: true }
          );
          this.organizationFormGroup.controls.yearlyTalkerQuota.setValue("",
            { emitEvent: false, onlySelf: true }
          );
        }),
        switchMap((unlimitedQuota) =>
          this.organizationRepository.patch(this.organization.id, {
            talkerQuota: unlimitedQuota ? null : 1,
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.snackBarService.pushMessage("Modification enregistrée", "success");
      });

    this.organizationFormGroup.controls.extraOrganizationMatching.valueChanges
    .pipe(
      tap(() => {
        (window._mtm || []).push({ 'event': 'clic-checkbox', 'checkbox-type': 'Autres organisations' });
      }),
      switchMap(() =>
        this.organizationRepository.patch(this.organization.id, {
          extraOrganizationMatching:
            this.organizationFormGroup.controls.extraOrganizationMatching.value
        })
      ),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.snackBarService.pushMessage("Modification enregistrée", "success");
    });

    this.organizationFormGroup.controls.intraOrganizationMatching.valueChanges
    .pipe(
      tap(() => {
        (window._mtm || []).push({ 'event': 'clic-checkbox', 'checkbox-type': 'Se rencontrer' });
      }),
      switchMap(() =>
        this.organizationRepository.patch(this.organization.id, {
          intraOrganizationMatching:
            this.organizationFormGroup.controls.intraOrganizationMatching.value,
        })
      ),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.snackBarService.pushMessage("Modification enregistrée", "success");
    });

    // Dynamic type enabling/disabling subscriptions
    this.organizationFormGroup.controls.speechEnabled.valueChanges
    .pipe(
      switchMap(() => {
        const forbiddenDynamicTypes = this.computeForbiddenDynamicTypes();
        return this.organizationRepository.patch(this.organization.id, {
          forbiddenDynamicTypes,
        });
      }),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.snackBarService.pushMessage("Modification enregistrée", "success");
    });

    this.organizationFormGroup.controls.coachingEnabled.valueChanges
    .pipe(
      switchMap(() => {
        const forbiddenDynamicTypes = this.computeForbiddenDynamicTypes();
        return this.organizationRepository.patch(this.organization.id, {
          forbiddenDynamicTypes,
        });
      }),
      takeUntil(this.destroy$)
    )
    .subscribe(() => {
      this.snackBarService.pushMessage("Modification enregistrée", "success");
    });
  }

  /**
   * Compute forbiddenDynamicTypes array based on checkbox states.
   * If speechEnabled is false, add 'speech' to forbidden list.
   * If coachingEnabled is false, add 'coaching' to forbidden list.
   */
  private computeForbiddenDynamicTypes(): DynamicType[] {
    const forbidden: DynamicType[] = [];
    if (!this.organizationFormGroup.controls.speechEnabled.value) {
      forbidden.push(DynamicType.SPEECH);
    }
    if (!this.organizationFormGroup.controls.coachingEnabled.value) {
      forbidden.push(DynamicType.COACHING);
    }
    return forbidden;
  }

  switchOrganization(organization: Organization) {
    this.router.navigate(["organizations", organization.id]);
  }

  protected readonly capitalize = capitalize;

  editThematics() {
    this.router.navigate(["thematics"], { relativeTo: this.route });
  }

  goToQuestions() {
    this.router.navigate(["questions"]);
  }

  addPilot() {
    this.router.navigate(["add-pilot"], { relativeTo: this.route });
  }

  updateForbiddenOrganizationsSelection(checkboxes: Checkbox[]): void {
    this.forbiddenOrganizationIdsSelection = checkboxes
      .filter((checkbox) => checkbox.selected)
      .map((checkbox) => checkbox.id);
    this.allowedOrganizationIdsSelection = checkboxes
      .filter((checkbox) => !checkbox.selected)
      .map((checkbox) => checkbox.id);

    this.organizationRepository
      .patch(this.organization.id, {
        allowedOrganizationIds: this.allowedOrganizationIdsSelection,
        forbiddenOrganizationIds: this.forbiddenOrganizationIdsSelection,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.snackBarService.pushMessage("Modification enregistrée", "success");
      });
  }

  addChild() {
    this.dialog
      .open(NewOrganizationComponent, this.organization)
      .pipe(takeUntil(this.destroy$))
      .subscribe((newOrganizationResult) => {
        const organizationPayload = newOrganizationResult?.organization;
        const subscription2026 = newOrganizationResult?.subscription2026;
        if (organizationPayload?.name) {
          this.organizationRepository.create(organizationPayload).subscribe({
            next: (organisationCreated) => {
              if (subscription2026) {
                this.organizationRepository.assignSubscription(
                  organisationCreated.id,
                  subscription2026,
                ).subscribe({
                  next: () => {
                    this.organizationRepository.findById(organisationCreated.id).subscribe({
                      next: (updatedOrganization) => {
                        this.snackBarService.pushMessage(
                          "Organisation et souscription 2026 créées avec succès",
                          "success"
                        );
                        this.organization.children?.push(updatedOrganization);
                      },
                    });
                  },
                  error: () => {
                    this.snackBarService.pushMessage(
                      "L'organisation a été créée, mais la souscription 2026 n'a pas pu être appliquée",
                      "error"
                    );
                    this.organization.children?.push(organisationCreated);
                  }
                });
                return;
              }

              this.snackBarService.pushMessage(
                "Organisation créée avec succès",
                "success"
              );
              this.organization.children?.push(organisationCreated);
            },
          });
        }
      });
  }

  editUnits() {
    if (this.profile?.role === Role.Talker && this.profile?.isPilot) {
      this.organizationRepository
        .findAll()
        .pipe(takeUntil(this.destroy$))
        .subscribe((organizations) => {
          this.dialog
            .open(AddUnitsComponent, {
              units: 0,
              organizations: flattenOrganizationTrees(organizations).filter(
                (o) => o.id !== this.organization.id
              ),
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe((payload) => {
              if (payload && payload.organizationId && payload.units) {
                this.organizationMembersRepository
                  .addUnitsFrom(payload.units, payload.organizationId)
                  .subscribe({
                    next: () => {
                      this.snackBarService.pushMessage(
                        "Modification enregistrée",
                        "success"
                      );
                      this.organization.units += Number(payload.units);
                    },
                  });
              }
            });
        });
    }
    if (this.profile?.role === Role.Admin) {
      this.organizationRepository
        .findAll()
        .pipe(takeUntil(this.destroy$))
        .subscribe((organizations) => {
          this.dialog
            .open(AddUnitsComponent, {
              units: 0,
              organizations: flattenOrganizationTrees(organizations).filter(
                (o) => o.id !== this.organization.id
              ),
              isAdmin: true,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe((payload) => {
              if (payload?.organizationId && payload?.units) {
                this.organizationMembersRepository
                  .addUnitsFrom(payload.units, payload.organizationId)
                  .subscribe({
                    next: () => {
                      this.snackBarService.pushMessage(
                        "Modification enregistrée",
                        "success"
                      );
                      this.organization.units += Number(payload.units);
                    },
                  });
              }
              if (!payload?.organizationId && payload?.units) {
                this.organizationMembersRepository
                  .addUnits(payload.units)
                  .subscribe({
                    next: () => {
                      this.snackBarService.pushMessage(
                        "Modification enregistrée",
                        "success"
                      );
                      this.organization.units += Number(payload.units);
                      this.units += Number(payload.units);
                    },
                  });
              }
            });
        });
    }
  }

  editName() {
    this.dialog
      .open(EditOrganizationNameComponent, { name: this.organization.name })
      .pipe(
        takeUntil(this.destroy$),
        filter((newName): newName is string => !!newName),
        switchMap((newName) =>
          this.organizationRepository.patch(this.organization.id, {
            name: newName,
          })
        )
      )
      .subscribe((organization) => {
        this.organization = organization;
        this.snackBarService.pushMessage("Nom modifié avec succès", "success");
      });
  }

  deleteOrganization(): void {
    this.dialog
      .open(ArchiveOrganizationDialog, {
        title: "Voulez-vous vraiment supprimer cet organisation ?",
        organization: this.organization,
        step: 0,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((isModified) => {
        if (isModified) {
          this.router.navigate(["organizations"]);
          this.snackBarService.pushMessage(
            "L'organisation a été supprimée avec succès",
            "success"
          );
        }
      });
  }

  deleteQuota() {
    (window._mtm || []).push({ 'event': 'nombre-quota', 'quota': 'Supprimer' });
  }

  onSelectQuotaClick() {
  }

  handleCheckboxChange(){
    (window._mtm || []).push({ 'event': 'clic-checkbox', 'checkbox-type': 'Illimité' });
  }

  selectPlan(plan: string) {
    if (this.hasManagedSubscription) {
      return;
    }
    const planSlug = this.getPlanSlugByName(plan);
    this.organizationRepository.patch(this.organization.id, {
      subscriptionPlan: planSlug,
      testEndDate: planSlug === 'tester-subscription' ? this.getDefaultTestEndDate() : undefined
    }).subscribe(() => {
      this.snackBarService.pushMessage("Modification enregistrée", "success");
      const metadata = getOrganizationPlanMetadataByPlanTagname(planSlug);
      this.subscriptionPlanMetadataSubject.next(metadata);
      this.organizationSubject.next({ ...this.organization, subscriptionPlan: { name: plan } });
      this.isTesterPlanSubject.next(planSlug === 'tester-subscription');    

    });
  }

  deletePlan() {
    this.organizationRepository.patch(this.organization.id, {
      subscriptionPlan: 'delete'
    }).subscribe(() => {
      this.snackBarService.pushMessage("Modification enregistrée", "success");
      this.subscriptionPlanMetadataSubject.next(undefined);
      this.organizationSubject.next({ ...this.organization, subscriptionPlan: undefined });
      this.isTesterPlanSubject.next(false);
    });
  }

  saveManagedSubscription() {
    const managedSubscriptionPayload = this.getManagedSubscriptionPayload();
    if (!managedSubscriptionPayload) {
      return;
    }

    const currentStartDate = this.organization.activeSubscription?.startsAt
      ? new Date(this.organization.activeSubscription.startsAt).toISOString().split("T")[0]
      : "";
    const selectedStartDate = this.organizationFormGroup.controls.managedSubscriptionStartsAt.value;
    const selectedPlanName = this.organizationFormGroup.controls.managedSubscriptionPlan.value;
    const currentPlanName = this.organization.activeSubscription
      ? this.getManagedPlanDisplayName(this.organization.activeSubscription.planCode)
      : "";

    const shouldUpdateExistingSubscription =
      !!this.organization.activeSubscription
      && selectedPlanName === currentPlanName
      && selectedStartDate === currentStartDate;

    const request$ = shouldUpdateExistingSubscription
      ? this.organizationRepository.updateSubscription(
          this.organization.id,
          this.organization.activeSubscription!.id,
          {
            seatCount: managedSubscriptionPayload.seatCount,
          }
        )
      : this.organizationRepository.assignSubscription(
          this.organization.id,
          managedSubscriptionPayload,
        );

    request$
      .pipe(
        switchMap(() => this.organizationRepository.findById(this.organization.id)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (organization) => {
          this.organization = organization;
          this.writeValue(organization);
          this.snackBarService.pushMessage(
            shouldUpdateExistingSubscription
              ? "Souscription 2026 mise à jour"
              : "Souscription 2026 enregistrée",
            "success"
          );
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.snackBarService.pushMessage(
            "La souscription 2026 n'a pas pu être enregistrée",
            "error"
          );
        }
      });
  }

  get subscriptionButtonLabel() {
    return this.organization.subscriptionPlan ? this.organization.subscriptionPlan.name : 'Ajouter une formule';
  }

  get subscriptionButtonIcon() {
    return this.organization.subscriptionPlan ? 'edit' : 'add';
  }

  get isPilot() {
    return this.profile?.role === Role.Talker && this.profile?.isPilot;
  }

  get hasManagedSubscription(): boolean {
    return !!this.organization?.activeSubscription;
  }

  get canSaveManagedSubscription(): boolean {
    return !!this.getManagedSubscriptionPayload();
  }

  get managedSubscriptionActionLabel(): string {
    if (!this.organization?.activeSubscription) {
      return "Créer la souscription 2026";
    }

    const currentStartDate = this.organization.activeSubscription.startsAt
      ? new Date(this.organization.activeSubscription.startsAt).toISOString().split("T")[0]
      : "";
    const selectedPlanName = this.organizationFormGroup.controls.managedSubscriptionPlan.value;
    const currentPlanName = this.getManagedPlanDisplayName(this.organization.activeSubscription.planCode);
    const selectedStartDate = this.organizationFormGroup.controls.managedSubscriptionStartsAt.value;

    return selectedPlanName === currentPlanName && selectedStartDate === currentStartDate
      ? "Mettre à jour la souscription 2026"
      : "Remplacer la souscription 2026";
  }

  get hasUnlimitedCredits(): boolean {
    return unlimitedCreditsSubscriptionPlans.includes(this.organization?.subscriptionPlan?.slug ?? '');
  }

  get currentManagedOfferCard(): CompanyOfferCard | undefined {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as OfferPlanCode | undefined;
    return getCompanyOfferCard(currentPlanCode);
  }

  get recommendedManagedOfferCard(): CompanyOfferCard | undefined {
    const snapshot = getOfferContextSnapshot(this.organization);
    const recommendedPlan = getRecommendedOfferPlan(snapshot.currentPlanCode);
    return recommendedPlan ? getCompanyOfferCard(recommendedPlan) : undefined;
  }

  get subscriptionPlanName(): string {
    return this.organization?.subscriptionPlan?.name ?? '';
  }

  goToCompanyOffers() {
    this.router.navigateByUrl("/offres-entreprises-2026");
  }

  getPlanSlugByName(name: string): string {
    return getPlanSlugByDisplayName(name);
  }

  getDefaultTestEndDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  }

  private getManagedSubscriptionPayload(): CreateOrganizationSubscription | undefined {
    const planVersionId =
      this.managedPlanVersionIdsByName[this.organizationFormGroup.controls.managedSubscriptionPlan.value];
    const startsAt = this.organizationFormGroup.controls.managedSubscriptionStartsAt.value;
    const seatCount = safeNumber(this.organizationFormGroup.controls.managedSubscriptionSeatCount.value);

    if (!planVersionId || !startsAt || seatCount <= 0) {
      return undefined;
    }

    return {
      planVersionId,
      startsAt: new Date(startsAt).toISOString(),
      seatCount,
    };
  }

  private getManagedPlanDisplayName(planCode: string): string {
    return getOrganizationPlanMetadataByPlanTagname(planCode)?.name ?? planCode;
  }

  private getTodayDate(): string {
    return new Date().toISOString().split("T")[0];
  }

  get creditInfo() {
    return buildCreditInfo(this.organization?.creditCosts, {
      orientationCallOffered: this.hasComplimentaryOrientationCall,
    });
  }

  get hasComplimentaryOrientationCall(): boolean {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as OfferPlanCode | undefined;
    return currentPlanCode === "wetalk"
      || currentPlanCode === "wetalk-plus"
      || currentPlanCode === "wetalk-coach";
  }

  modifyTestEndDate(){
    this.organizationRepository.patch(
      this.organization.id, 
      {
        testEndDate: this.organization.subscriptionPlan?.slug === 'tester-subscription' 
          ? this.organizationFormGroup.controls.testEndDate.value 
          : undefined
      }
    ).subscribe(() => {
      this.snackBarService.pushMessage("Modification enregistrée", "success");
    });
  }
}
